import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Loader2, ArrowLeft, BarChart3, Table, Info } from 'lucide-react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { getTagReport, TagReportParams, TagReportResponse } from '@/api/tagReportApi';
import { format, subDays } from 'date-fns';
import TableContainer from '@/components/TableContainerReactTable';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const DIMENSION_OPTIONS = [
    { value: 'date', label: 'Por Data' },
    { value: 'ad_unit', label: 'Por Bloco de Anúncio' },
    { value: 'url', label: 'Por URL' },
];

const formatNumber = (num: number) => {
    return new Intl.NumberFormat('pt-BR').format(num);
};

const TagReport = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const today = format(new Date(), 'yyyy-MM-dd');
    const thirtyDaysAgo = format(subDays(new Date(), 30), 'yyyy-MM-dd');

    const [startDate, setStartDate] = useState(thirtyDaysAgo);
    const [endDate, setEndDate] = useState(today);
    const [dimension, setDimension] = useState<'ad_unit' | 'date' | 'url'>('date');
    const [report, setReport] = useState<TagReportResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const validateDates = (): string | null => {
        if (!startDate || !endDate) return 'Selecione as datas de início e fim';
        if (new Date(startDate) > new Date(endDate)) return 'A data de início não pode ser posterior à data fim';
        const diffMs = new Date(endDate).getTime() - new Date(startDate).getTime();
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        if (diffDays > 365) return 'O intervalo máximo permitido é de 365 dias';
        return null;
    };

    const fetchReport = async () => {
        if (!id) return;
        const validationError = validateDates();
        if (validationError) {
            setError(validationError);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const params: TagReportParams = { startDate, endDate, dimension };
            const data = await getTagReport(id, params);
            setReport(data);
        } catch (err: any) {
            const msg = err?.response?.data?.message;
            setError(Array.isArray(msg) ? msg.join(', ') : msg || 'Erro ao carregar relatório');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const handleGenerateReport = () => {
        fetchReport();
    };

    // Chart options
    const chartOptions: Highcharts.Options = useMemo(() => {
        if (!report?.data?.length) return {};

        const categories = report.data.map((row) => row.dimension);
        const requestsSeries = report.data.map((row) => row.requests);
        const impressionsSeries = report.data.map((row) => row.impressions);
        const fillRateSeries = report.data.map((row) => row.fillRate);

        return {
            chart: { type: 'line', height: 350 },
            title: { text: undefined },
            xAxis: {
                categories,
                labels: {
                    rotation: categories.length > 15 ? -45 : 0,
                    style: { fontSize: '11px' },
                },
            },
            yAxis: [
                {
                    title: { text: 'Quantidade' },
                    labels: {
                        formatter: function () {
                            return formatNumber(this.value as number);
                        },
                    },
                },
                {
                    title: { text: 'Taxa de Preenchimento (%)' },
                    opposite: true,
                    min: 0,
                    max: 100,
                    labels: { format: '{value}%' },
                },
            ],
            tooltip: {
                shared: true,
                useHTML: true,
                formatter: function (this: any) {
                    const points = (this as any).points || [];
                    let html = `<b>${this.x}</b><br/>`;
                    points.forEach((point: any) => {
                        const value = point.series.name === 'Taxa de Preenchimento'
                            ? `${point.y?.toFixed(2)}%`
                            : formatNumber(point.y || 0);
                        html += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${value}</b><br/>`;
                    });
                    return html;
                },
            },
            legend: { enabled: true },
            credits: { enabled: false },
            series: [
                {
                    name: 'Requisições',
                    type: 'line',
                    data: requestsSeries,
                    color: '#4361ee',
                    yAxis: 0,
                },
                {
                    name: 'Impressões',
                    type: 'line',
                    data: impressionsSeries,
                    color: '#2ec4b6',
                    yAxis: 0,
                },
                {
                    name: 'Taxa de Preenchimento',
                    type: 'line',
                    data: fillRateSeries,
                    color: '#ff6b6b',
                    dashStyle: 'ShortDash',
                    yAxis: 1,
                },
            ],
        };
    }, [report]);

    // Table columns
    const columns = useMemo(() => {
        const dimensionLabel = DIMENSION_OPTIONS.find((d) => d.value === dimension)?.label || 'Dimensão';
        return [
            {
                header: dimensionLabel,
                accessorKey: 'dimension',
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: 'Requisições',
                accessorKey: 'requests',
                cell: (cell: any) => formatNumber(cell.getValue()),
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: 'Impressões',
                accessorKey: 'impressions',
                cell: (cell: any) => formatNumber(cell.getValue()),
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: 'Taxa de Preenchimento',
                accessorKey: 'fillRate',
                cell: (cell: any) => `${cell.getValue()?.toFixed(2)}%`,
                enableColumnFilter: false,
                enableSorting: true,
            },
        ];
    }, [dimension]);

    document.title = report?.tag?.name
        ? `Relatório - ${report.tag.name} | Ad Pulse`
        : 'Relatório da TAG | Ad Pulse';

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={() => navigate('/tag')}>
                    <ArrowLeft className="h-4 w-4" />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">
                        Relatório da TAG
                        {report?.tag?.name && (
                            <span className="font-normal text-muted-foreground"> — {report.tag.name}</span>
                        )}
                    </h1>
                    {report?.tag?.domain && (
                        <p className="text-sm text-muted-foreground">{report.tag.domain}</p>
                    )}
                </div>
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                        <div className="space-y-2">
                            <Label htmlFor="startDate">Data Início</Label>
                            <Input
                                type="date"
                                id="startDate"
                                value={startDate}
                                max={endDate || today}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="endDate">Data Fim</Label>
                            <Input
                                type="date"
                                id="endDate"
                                value={endDate}
                                min={startDate}
                                max={today}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="dimension">Dimensão</Label>
                            <Select value={dimension} onValueChange={(value: any) => setDimension(value)}>
                                <SelectTrigger id="dimension">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {DIMENSION_OPTIONS.map((opt) => (
                                        <SelectItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button onClick={handleGenerateReport} disabled={loading} className="w-full">
                            {loading ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <BarChart3 className="mr-2 h-4 w-4" />
                            )}
                            Gerar Relatório
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Error */}
            {error && (
                <Alert variant="destructive">
                    <Info className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            {/* Loading */}
            {loading && (
                <Card>
                    <CardContent className="flex h-64 items-center justify-center">
                        <div className="text-center">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-2" />
                            <p className="text-muted-foreground">Carregando relatório...</p>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Results */}
            {!loading && report && (
                <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card>
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-4">
                                    <div className="rounded-lg bg-blue-100 p-3 dark:bg-blue-900/20">
                                        <BarChart3 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground">Requisições</p>
                                        <p className="text-2xl font-bold">{formatNumber(report.totals.requests)}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-4">
                                    <div className="rounded-lg bg-emerald-100 p-3 dark:bg-emerald-900/20">
                                        <Table className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground">Impressões</p>
                                        <p className="text-2xl font-bold">{formatNumber(report.totals.impressions)}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-4">
                                    <div className="rounded-lg bg-amber-100 p-3 dark:bg-amber-900/20">
                                        <Info className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground">Taxa de Preenchimento</p>
                                        <p className="text-2xl font-bold">{report.totals.fillRate.toFixed(2)}%</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Chart */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <BarChart3 className="h-5 w-5" />
                                Gráfico de Desempenho
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {report.data.length > 0 ? (
                                <HighchartsReact
                                    highcharts={Highcharts}
                                    options={chartOptions}
                                />
                            ) : (
                                <div className="flex h-48 items-center justify-center text-muted-foreground">
                                    <div className="text-center">
                                        <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                        <p>Nenhum dado encontrado para o período selecionado</p>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Table */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Table className="h-5 w-5" />
                                Detalhamento
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {report.data.length > 0 ? (
                                <TableContainer
                                    divClass="table-responsive"
                                    columns={columns}
                                    data={report.data}
                                    isGlobalFilter={true}
                                    customPageSize={30}
                                    SearchPlaceholder="Procurar..."
                                />
                            ) : (
                                <div className="flex h-48 items-center justify-center text-muted-foreground">
                                    Nenhum dado encontrado para o período selecionado
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
};

export default TagReport;
